import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DISCLAIMER_TEXT = `Questa app è uno strumento di auto-riflessione e monitoraggio personale. Non sostituisce consulenza psicologica o medica professionale.

In caso di disagio significativo, rivolgiti a un professionista qualificato. In emergenza, contatta i servizi di emergenza.`;

const PRIVACY_TEXT = `Titolare: Laura Piccinini - Laboratorio di Psicologia Applicata

I tuoi dati (spese, abitudini, umore, diario) sono visibili solo a te. Non condividiamo dati con terze parti.

Puoi richiedere la cancellazione scrivendo a piccinini.ps@gmail.com`;

// Componente Onboarding Dialog
export const OnboardingDialog = ({ open, onComplete }) => {
  const [understood, setUnderstood] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleComplete = () => {
    if (understood && privacyAccepted) {
      localStorage.setItem("life_planner_onboarding_completed", "true");
      onComplete();
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-md rounded-sm" hideCloseButton>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Benvenuto in Life Planner</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          <div className="border border-border rounded-sm p-3">
            <h3 className="font-semibold text-sm mb-1">Disclaimer</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {DISCLAIMER_TEXT}
            </p>
          </div>

          <div className="border border-border rounded-sm p-3">
            <h3 className="font-semibold text-sm mb-1">Privacy</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {PRIVACY_TEXT}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="understood" 
                checked={understood}
                onCheckedChange={setUnderstood}
                data-testid="onboarding-checkbox"
              />
              <label htmlFor="understood" className="text-sm cursor-pointer">
                Ho letto il disclaimer
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox 
                id="privacy" 
                checked={privacyAccepted}
                onCheckedChange={setPrivacyAccepted}
                data-testid="privacy-checkbox"
              />
              <label htmlFor="privacy" className="text-sm cursor-pointer">
                Accetto la privacy policy
              </label>
            </div>
          </div>

          <Button 
            onClick={handleComplete}
            disabled={!understood || !privacyAccepted}
            className="w-full rounded-sm"
            data-testid="onboarding-continue-btn"
          >
            Continua
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Hook per controllare se l'onboarding è stato completato
export const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem("life_planner_onboarding_completed");
    if (!completed) {
      setShowOnboarding(true);
    }
    setChecked(true);
  }, []);

  const completeOnboarding = () => {
    setShowOnboarding(false);
  };

  return { showOnboarding, completeOnboarding, checked };
};

// Componente Footer Disclaimer (breve) per le pagine di tracking
export const DisclaimerFooter = () => {
  const [showFull, setShowFull] = useState(false);

  return (
    <>
      <div className="mt-8 pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">
          Strumento di auto-riflessione, non sostituisce consulenza professionale.{" "}
          <button 
            onClick={() => setShowFull(true)}
            className="underline hover:text-foreground"
            data-testid="disclaimer-info-btn"
          >
            Maggiori info
          </button>
        </p>
      </div>

      <Dialog open={showFull} onOpenChange={setShowFull}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Informazioni Importanti</DialogTitle>
          </DialogHeader>
          <div className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
            {DISCLAIMER_TEXT}
          </div>
          <Button onClick={() => setShowFull(false)} className="rounded-sm">
            Ho capito
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Componente Icona Info per header delle pagine
export const DisclaimerInfoIcon = () => {
  const [showFull, setShowFull] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowFull(true)}
        className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-xs text-muted-foreground hover:bg-muted transition-colors"
        title="Informazioni importanti"
        data-testid="disclaimer-icon-btn"
      >
        i
      </button>

      <Dialog open={showFull} onOpenChange={setShowFull}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Informazioni Importanti</DialogTitle>
          </DialogHeader>
          <div className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
            {DISCLAIMER_TEXT}
          </div>
          <Button onClick={() => setShowFull(false)} className="rounded-sm">
            Ho capito
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Testi esportati per uso in altre pagine
export const DISCLAIMER_FULL = DISCLAIMER_TEXT;
export const PRIVACY_FULL = PRIVACY_TEXT;

export default OnboardingDialog;
